/**
 * MenuItemCard - Uber Eats-style inline customization component
 * Expands in-place when clicked to show customization options
 * Enhanced with sticky add-to-cart bar, image zoom, and micro-interactions
 * 
 * Refactored to use sub-components for better maintainability:
 * - ItemPreview, CustomizationPanel, useMenuItemState (custom hook)
 * - SizeSelector, ModifierSelector, IngredientList, AddOnSelector
 * - QuantitySelector, PopularCustomizations, StickyCartBar, ImageZoomModal
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { toast } from 'sonner';

// Sub-components
import {
  StickyCartBar,
  ImageZoomModal,
  ItemPreview,
  CustomizationPanel,
  useMenuItemState
} from './menu-item';

export default function MenuItemCard({ item, availableAddOns = [], onClose, onAddToCart, onExpandChange }) {
  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  
  // Refs
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const addToCartButtonRef = useRef(null);
  
  // Hooks
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { addFavorite, isFavorite } = useFavorites();
  
  // Custom hook for all customization state and logic
  const {
    customization,
    loadingCustomization,
    popularCustomizations,
    ingredients,
    modifiers,
    modifierGroups,
    sizes,
    hasCustomizations,
    compatibleAddOns,
    selectedSize,
    setSelectedSize,
    selectedModifiers,
    removedIngredients,
    extraIngredients,
    selectedAddOns,
    specialNotes,
    setSpecialNotes,
    quantity,
    setQuantity,
    finalPrice,
    isCoreIngredient,
    getModifierPrice,
    formatPriceRange,
    getIngredientStepNumber,
    fetchCustomization,
    fetchPopularCustomizations,
    applyPopularCustomization,
    toggleModifier,
    selectModifierGroupOption,
    deselectModifierGroupOption,
    toggleRemoveIngredient,
    toggleExtraIngredient,
    toggleAddOn,
    increaseAddOnQuantity,
    decreaseAddOnQuantity,
    resetSelections
  } = useMenuItemState(item, availableAddOns);

  // Effects
  useEffect(() => {
    if (onExpandChange) onExpandChange(isExpanded);
  }, [isExpanded, onExpandChange]);

  useEffect(() => {
    if (isExpanded && !customization) {
      fetchCustomization();
      fetchPopularCustomizations();
    }
  }, [isExpanded, customization, fetchCustomization, fetchPopularCustomizations]);

  useEffect(() => {
    if (isExpanded && cardRef.current) {
      setTimeout(() => {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded || !addToCartButtonRef.current) {
      setShowStickyBar(false);
      return;
    }
    
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0.1, rootMargin: '-80px 0px -100px 0px' }
    );
    
    observer.observe(addToCartButtonRef.current);
    return () => observer.disconnect();
  }, [isExpanded, loadingCustomization]);

  // Video visibility observer
  useEffect(() => {
    if (!item.video || !videoRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      },
      { threshold: 0.3 }
    );
    
    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [item.video]);

  // Event handlers
  const handleAddToCart = () => {
    setIsAddingToCart(true);
    setTimeout(() => setIsAddingToCart(false), 400);
    
    const modifiersArray = selectedModifiers.map(mod => ({
      modifier_name: mod.name,
      selected_option: mod.name,
      price_adjustment: mod.price || 0
    }));

    for (let i = 0; i < quantity; i++) {
      addToCart(
        item,
        selectedSize,
        modifiersArray.length > 0 ? modifiersArray : null,
        specialNotes,
        removedIngredients.length > 0 ? removedIngredients : null,
        extraIngredients.length > 0 ? extraIngredients : null,
        selectedAddOns.length > 0 ? selectedAddOns : null
      );
    }

    toast.success(`${quantity}x ${item.name} added to cart!`);
    resetAndClose();
    
    if (onAddToCart) {
      setTimeout(() => onAddToCart(), 100);
    }
  };

  const handleAddAsIs = (e) => {
    e.stopPropagation();
    setIsAddingToCart(true);
    setTimeout(() => setIsAddingToCart(false), 400);
    
    // Use BASE item.price - sizes are optional upsells, not defaults
    // Customer must explicitly select a size to get that price
    const defaultPrice = item.price;
    
    addToCart({ ...item, price: defaultPrice }, null, null, null, null, null, null);
    toast.success(`${item.name} added to cart as-is!`);
    
    if (onAddToCart) setTimeout(() => onAddToCart(), 100);
  };

  const resetAndClose = () => {
    setIsExpanded(false);
    resetSelections();
  };

  const handleSaveFavorite = async () => {
    const modifiersArray = selectedModifiers.map(mod => ({
      modifier_name: mod.name,
      selected_option: mod.name,
      price_adjustment: mod.price || 0
    }));
    
    await addFavorite(
      item, 
      selectedSize, 
      modifiersArray.length > 0 ? modifiersArray : null, 
      specialNotes,
      extraIngredients.length > 0 ? extraIngredients : null,
      removedIngredients.length > 0 ? removedIngredients : null
    );
  };

  return (
    <div
      ref={cardRef}
      className={`menu-card group bg-card rounded-3xl overflow-hidden shadow-lg border transition-all duration-300 ${
        isExpanded ? 'border-primary shadow-xl' : 'border-border hover:shadow-xl'
      }`}
      data-testid={`menu-item-${item.id}`}
    >
      {/* Collapsed View - Item Preview */}
      <ItemPreview
        item={item}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        hasCustomizations={hasCustomizations}
        formatPriceRange={formatPriceRange}
        videoRef={videoRef}
        isAddingToCart={isAddingToCart}
        handleAddAsIs={handleAddAsIs}
        setShowImageZoom={setShowImageZoom}
      />

      {/* Expanded View - Customization Options */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-border overflow-hidden"
          >
            <CustomizationPanel
              loadingCustomization={loadingCustomization}
              popularCustomizations={popularCustomizations}
              sizes={sizes}
              modifiers={modifiers}
              modifierGroups={modifierGroups}
              ingredients={ingredients}
              compatibleAddOns={compatibleAddOns}
              selectedSize={selectedSize}
              selectedModifiers={selectedModifiers}
              removedIngredients={removedIngredients}
              extraIngredients={extraIngredients}
              selectedAddOns={selectedAddOns}
              specialNotes={specialNotes}
              quantity={quantity}
              finalPrice={finalPrice}
              isAddingToCart={isAddingToCart}
              isAuthenticated={isAuthenticated}
              isFavorite={isFavorite}
              itemId={item.id}
              onApplyCustomization={applyPopularCustomization}
              onSelectSize={setSelectedSize}
              onToggleModifier={toggleModifier}
              onSelectModifierGroupOption={selectModifierGroupOption}
              onDeselectModifierGroupOption={deselectModifierGroupOption}
              onToggleRemove={toggleRemoveIngredient}
              onToggleExtra={toggleExtraIngredient}
              onToggleAddOn={toggleAddOn}
              onIncreaseAddOnQuantity={increaseAddOnQuantity}
              onDecreaseAddOnQuantity={decreaseAddOnQuantity}
              setSpecialNotes={setSpecialNotes}
              setQuantity={setQuantity}
              handleAddToCart={handleAddToCart}
              handleSaveFavorite={handleSaveFavorite}
              resetAndClose={resetAndClose}
              getModifierPrice={getModifierPrice}
              isCoreIngredient={isCoreIngredient}
              getIngredientStepNumber={getIngredientStepNumber}
              addToCartButtonRef={addToCartButtonRef}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Add to Cart Bar */}
      <AnimatePresence>
        {isExpanded && showStickyBar && (
          <StickyCartBar 
            item={item}
            selectedSize={selectedSize}
            quantity={quantity}
            finalPrice={finalPrice}
            isAddingToCart={isAddingToCart}
            onAddToCart={handleAddToCart}
          />
        )}
      </AnimatePresence>

      {/* Image Zoom Modal */}
      <ImageZoomModal 
        isOpen={showImageZoom}
        onClose={setShowImageZoom}
        item={item}
        formatPriceRange={formatPriceRange}
      />
    </div>
  );
}
